import { apiPublica } from '../instancia-api-publica'

const urlBase = '/propostamatricula/publica'

/* ==========================================================================
 * Tipos
 *
 * Espelham os DTOs reais de
 * Ipgs.Educacional.Application/Queries/Propostas/ObterPropostaPublica e
 * Commands/Propostas/AceitarProposta — conferidos contra o C#, não contra o PDF
 * de contrato de dados.
 *
 * Onde os dois divergem, a diferença é intencional e está anotada: o PDF
 * descreve um `opcoes[]` único com campo `destino`, e o backend devolve dois
 * arrays (`opcoesEntrada`/`opcoesRestante`). Carregam a mesma informação, e o
 * destino fica implícito em qual array a opção veio — o que elimina a chance de
 * um `destino` inconsistente com o agrupamento.
 * ======================================================================== */

/** Espelha OpcaoPublicaDto. */
export interface OpcaoPublicaDto {
  /** É o que o aceite devolve para escolher esta opção. Obrigatório. */
  id: string
  /** Nome do enum FormaPagamento ("CartaoCredito", "Pix", "Boleto", "Recorrente"). */
  formaPagamento: string
  /**
   * O enum FormaPagamento não tem [Description] no backend, então isto volta
   * com o próprio nome do enum. Quem traduz é `descreverFormaPagamento`.
   */
  formaPagamentoPorExtenso: string
  valor: number
  quantidadeParcelas: number
  valorParcela: number
  /** Calculado (valorTabela - valor), não persistido. É o argumento de venda. */
  valorDesconto: number
}

/** Espelha PlanoPublicoDto. */
export interface PlanoPublicoDto {
  /** Preço de tabela. Base do "De R$ 9.800,00" riscado e do selo de desconto. */
  valorTabela: number
  taxaMatricula: number
  /**
   * Isenção marcada pelo consultor ("Isentar taxa de matrícula"). true: a taxa
   * aparece como isenta e não entra no total. false: linha somada ao total.
   */
  taxaMatriculaInclusa: boolean
  /** false: o grupo "Entrada" é ocultado e o total considera só o restante. */
  temEntrada: boolean
  /** null → a primeira parcela é cobrada junto da matrícula. */
  dataInicioRecorrencia?: string
  /**
   * Identifica esta versão da composição. Devolvida no aceite: se o consultor
   * alterou preços ou opções depois que a tela abriu, o aceite é recusado e a
   * tela recarrega a condição.
   */
  assinatura?: string
  opcoesEntrada: OpcaoPublicaDto[]
  opcoesRestante: OpcaoPublicaDto[]
}

/**
 * Espelha PropostaPublicaDto (`GET /api/PropostaMatricula/publica/{id}`).
 *
 * Não traz `validadeAte` (D9 — a data de expiração nunca é exposta ao aluno)
 * nem `status`: o backend resolve os estados de recusa antes de montar o DTO, e
 * devolve 400 com mensagem. Ver `erros-condicao-especial.ts`.
 */
export interface CondicaoEspecialPublicaDto {
  id: string
  numeroProtocolo: string
  /** Exibido como saudação; é o nome do contato, não necessariamente do aluno. */
  nomeContato?: string

  turmaId?: string
  /** Código da turma ("NCF-2027-1"), não o nome. */
  turma?: string
  cursoId?: string
  curso?: string

  /** Nome do enum Modalidade — traduzido na tela, como formaPagamento. */
  modalidade?: string
  cargaHoraria: number
  duracao?: number

  /** Assina a proposta na tela ("Sua matrícula, montada por Bruna Souza"). */
  consultor?: string
  /** Opcional. Sem ele, as telas de erro não exibem botão de WhatsApp. */
  telefoneConsultor?: string

  /** true quando o consultor pré-informou um CPF e o aceite vai conferi-lo. */
  exigeCpf: boolean

  plano?: PlanoPublicoDto
}

/** Espelha EnderecoDto. */
export interface EnderecoRequest {
  pais: string
  cep: string
  logradouro: string
  numero: string
  complemento?: string
  bairro: string
  cidade: string
  uf: string
}

/**
 * Espelha DadosCartaoDto (Commands/Matriculas/Comum). Os nomes seguem o C#:
 * `codigo` é o CVV e `parcelas` é obrigatório.
 *
 * Só é enviado na Fase B — hoje o aceite não captura cartão (gaps D23/D24), e
 * a cobrança nasce AVencer.
 */
export interface DadosCartaoRequest {
  nomeImpresso: string
  numero: string
  validade: string
  codigo: string
  parcelas: number
}

/**
 * Corpo de `POST /api/PropostaMatricula/publica/{id}/conta` — cria a conta do
 * aluno antes do aceite. Espelha CriarContaPropostaCommand.
 */
export interface CriarContaRequest {
  nome: string
  nomeSocial?: string
  cpf: string
  email: string
  celular: string
  senha: string
  repetirSenha: string

  nacionalidade: string
  documentoIdentidade?: string
  codigoConselho?: string
  dataNascimento: string
  corRaca: number
  escolaridade: number
  mesAnoColacaoGrau?: string
  profissao?: string
  profissaoOutros?: string

  /** Ausente na criação: o endereço chega no PATCH seguinte. */
  endereco?: EnderecoRequest
}

export interface CriarContaResposta {
  alunoId: string
  usuarioId: string
  /** JWT: a partir daqui a tela fala pelo gateway autenticado. */
  token: string
}

/**
 * Corpo de `PATCH .../conta`. Espelha AtualizarContaPropostaCommand.
 *
 * Nome, CPF, documento e data de nascimento passaram a ser editáveis em
 * 02/10/2026: o aluno que errou esses dados no cadastro original precisa poder
 * corrigi-los durante a matrícula, senão o erro só aparece adiante (ex. recusa
 * do cartão por CPF divergente). O backend valida a unicidade do CPF e grava
 * trilha de auditoria da troca.
 */
export interface AtualizarContaRequest {
  nome: string
  cpf?: string
  documentoIdentidade?: string
  dataNascimento?: string
  nacionalidade: string
  nomeSocial?: string
  codigoConselho?: string
  email: string
  celular: string
  escolaridade: number
  mesAnoColacaoGrau?: string
  profissao?: string
  profissaoOutros?: string
  /** Ausente enquanto o aluno não chegou na seção de residência. */
  endereco?: EnderecoRequest
}

/**
 * Corpo de `POST /api/PropostaMatricula/publica/{id}/aceitar`.
 *
 * Espelha AceitarPropostaCommand. Não traz dados pessoais: o aceite é
 * AUTENTICADO, e o aluno vem do token — a conta já foi criada no bloco 2, ou o
 * aluno entrou numa existente.
 */
export interface AceitarCondicaoEspecialRequest {
  /** `plano.assinatura` da condição que o aluno está vendo. */
  assinaturaPlano?: string
  /** Obrigatória quando o plano prevê entrada; recusada quando não prevê. */
  opcaoEntradaId?: string
  opcaoRestanteId: string

  /** Enviado quando o restante é no cartão (CartaoCredito ou Recorrente). */
  dadosCartao?: DadosCartaoRequest
}

/**
 * Boleto emitido no aceite. Só vem para as formas Boleto e Pix (BolePIX); em
 * cartão e recorrência a lista volta vazia e a tela explica a cobrança.
 */
export interface BoletoEmitidoDto {
  /** "Entrada", "TaxaMatricula" ou "Restante" — decide o rótulo do cartão na confirmação. */
  destino: string
  valor: number
  vencimento: string
  /** Linha digitável já formatada pelo backend, com os espaços dos campos. */
  linhaDigitavel?: string
  /** URL do PDF no PJBank. Ausente enquanto o registro não completa. */
  urlPdf?: string
  /** "Boleto" ou "Pix" (BolePIX): decide se a confirmação mostra o QR Code. */
  formaPagamento?: string
  /** Pix: código do QR Code e do "copia e cola". Ausente se o PJBank não devolveu. */
  pixCopiaECola?: string
}

/** Espelha AceitarPropostaCommandResult. */
export interface AceitarCondicaoEspecialResposta {
  matriculaId: string
  alunoId: string
  /** JWT para login automático logo após o aceite. */
  token: string
  /**
   * Código do contrato. A confirmação não o exibe: o sequencial só nasce quando a
   * matrícula quita, então antes disso ele vinha só com o prefixo da modalidade
   * ("0003") para todo mundo.
   */
  numeroMatricula?: string
  boletos?: BoletoEmitidoDto[]
  /** Matrícula já concluída no aceite (1ª cobrança de ingresso paga no cartão): libera o acesso. */
  acessoLiberado?: boolean
}

/* ==========================================================================
 * Chamadas
 * ======================================================================== */

export const ObterCondicaoEspecialPublica = async (
  id: string
): Promise<CondicaoEspecialPublicaDto> => {
  const { data } = await apiPublica.get<CondicaoEspecialPublicaDto>(
    `${urlBase}/${id}`
  )

  return data
}

/**
 * Cria a conta do aluno. Ainda anônimo — é justamente a chamada que produz a
 * sessão.
 */
export const CriarContaCondicaoEspecial = async (
  id: string,
  dados: CriarContaRequest
): Promise<CriarContaResposta> => {
  const { data } = await apiPublica.post<CriarContaResposta>(
    `${urlBase}/${id}/conta`,
    dados
  )

  return data
}

/**
 * Cadastro do aluno logado. Espelha ContaPropostaDto.
 *
 * Os enums voltam como STRING (nome do enum), como no resto da API.
 */
export interface ContaPropostaDto {
  nome?: string
  nomeSocial?: string
  cpf?: string
  documentoIdentidade?: string
  codigoConselho?: string
  dataNascimento?: string
  email?: string
  celular?: string
  nacionalidade?: string
  corRaca?: string
  escolaridade?: string
  profissao?: string
  profissaoOutros?: string
  mesAnoColacaoGrau?: string
  endereco?: {
    cep?: string
    logradouro?: string
    numero?: string
    complemento?: string
    bairro?: string
    cidade?: string
    uf?: string
  }
}

/**
 * Busca o cadastro do aluno logado.
 *
 * Endpoint próprio porque `GET /api/usuario/{id}` é restrito ao backoffice
 * (Academico/Comercial/Parceiro) — um aluno recebe 403 nele. Aqui não há id: o
 * aluno vem do token.
 */
export const ObterContaCondicaoEspecial = async (): Promise<ContaPropostaDto> => {
  const { data } = await apiPublica.get<ContaPropostaDto>(`${urlBase}/conta`)

  return data
}

/** Resposta de `GET .../situacao-aluno`. */
export interface SituacaoAlunoProposta {
  jaMatriculadoNaTurma: boolean
  jaConcluiuEstaProposta: boolean
  /**
   * Quem está logado é o consultor que criou a proposta: a tela abre só para
   * visualização, sem criar matrícula nem gravar cadastro.
   */
  ehConsultorDaProposta: boolean
}

/**
 * Situacao do aluno logado nesta condicao especial.
 *
 * Consultada assim que ele autentica: avisar "voce ja esta nesta turma" no
 * comeco evita que ele preencha os cinco blocos para descobrir no ultimo clique
 * que nao precisava.
 *
 * Nunca lanca: e consulta de apoio, e o aceite continua sendo a autoridade
 * final. Falhar aqui nao pode tirar o aluno do fluxo.
 */
export const ObterSituacaoAlunoCondicaoEspecial = async (
  id: string
): Promise<SituacaoAlunoProposta | null> => {
  try {
    const { data } = await apiPublica.get<SituacaoAlunoProposta>(
      `${urlBase}/${id}/situacao-aluno`
    )

    return data
  } catch {
    return null
  }
}

/**
 * Previa do contrato + ficha, para o aluno ler antes de aceitar.
 *
 * Pela `apiPublica`, e nao pelo `gatewayApi`: o interceptor do gateway chama
 * `sair()` em QUALQUER 401 e navega para fora, o que tirava o aluno do meio da
 * matricula (ver publica/sessao.ts). Devolve uma object URL para o PdfViewer, ou
 * `null` quando o contrato ainda nao esta disponivel.
 */
export const ObterContratoCondicaoEspecial = async (
  id: string,
  opcaoRestanteId?: string,
  opcaoEntradaId?: string
): Promise<string | null> => {
  try {
    // As opções escolhidas vão como query para a ficha do PDF refletir o que o
    // aluno selecionou (sem elas, o backend mostra a opção de menor valor).
    const resposta = await apiPublica.get(`${urlBase}/${id}/contrato`, {
      headers: { Accept: 'application/pdf' },
      responseType: 'blob',
      params: {
        ...(opcaoRestanteId ? { opcaoRestanteId } : {}),
        ...(opcaoEntradaId ? { opcaoEntradaId } : {})
      }
    })

    if (resposta?.status !== 200) return null

    return URL.createObjectURL(resposta.data)
  } catch {
    // A previa e um extra: nao bloqueia o aceite se falhar.
    return null
  }
}

/**
 * Atualiza os dados do aluno a cada "Continuar".
 *
 * Autenticado, mas pela `apiPublica`: o token é aplicado nela por
 * `publica/sessao.ts`. Ver lá o porquê de não ser o `gatewayApi`.
 */
export const AtualizarContaCondicaoEspecial = async (
  id: string,
  dados: AtualizarContaRequest
): Promise<void> => {
  await apiPublica.patch(`${urlBase}/${id}/conta`, dados)
}

/** Aceite do contrato. Autenticado — ver CriarContaCondicaoEspecial. */
export const AceitarCondicaoEspecialPublica = async (
  id: string,
  dados: AceitarCondicaoEspecialRequest
): Promise<AceitarCondicaoEspecialResposta> => {
  const { data } = await apiPublica.post<AceitarCondicaoEspecialResposta>(
    `${urlBase}/${id}/aceitar`,
    dados
  )

  return data
}
